import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Modal,
  ModalContent,
  Loading,
  Button,
  Checkbox,
  ArrowBackwardIcon,
  ArrowForwardIcon,
} from '@clearcover/atlas';
import { CategorySection } from 'Shared/components/DiffViewer';
import { InfoValuePairDataType } from 'Shared/components/Card/CardSectionInfoPairs/InfoPair/InfoPair';
import { formatSummaryElement } from 'Policies/components/PolicyActivities/Common';
import { usePolicyContext } from 'Policies/utils';
import { useDiffData } from './diffService';
import { VERSION_TYPE_LABELS, VersionType, selectCreatedBy } from './types';
import styles from './VersionDiffModal.module.css';

interface VersionDiffModalProps {
  policyId: string;
  versionId: string;
  versionType: VersionType;
  source: 'version' | 'draft';
  createdBy: string;
  createdAt?: string;
  open: boolean;
  onClose: () => void;
  versionNumber?: number;
  effectiveStart?: string;
  isObsolete?: boolean;
  skipObsolete?: boolean;
  onToggleSkipObsolete?: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  isPrevDisabled?: boolean;
  isNextDisabled?: boolean;
  navPosition?: number;
  navTotal?: number;
  navPageItems?: Array<{
    position: number;
    isObsolete: boolean;
    label?: string;
  }>;
  onSelectNavPosition?: (position: number) => void;
}

function formatMoney(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

function renderPremiumChange(premiumChange?: {
  oldValue: number;
  newValue: number;
  delta: number;
}): InfoValuePairDataType {
  if (!premiumChange) {
    return undefined;
  }

  const { oldValue, newValue, delta } = premiumChange;
  const isIncrease = delta > 0;
  const isDecrease = delta < 0;
  const deltaClassName = isIncrease
    ? styles.premiumDeltaPositive
    : isDecrease
      ? styles.premiumDeltaNegative
      : styles.premiumDeltaNeutral;
  const arrowClassName = isIncrease
    ? styles.premiumArrowIncrease
    : isDecrease
      ? styles.premiumArrowDecrease
      : styles.premiumArrowNeutral;

  return {
    display: (
      <div className={styles.premiumSummary}>
        <div className={styles.premiumPrimaryLine}>
          <span>{formatMoney(newValue)}</span>
          <span className={styles.premiumDivider}>/</span>
          <span className={styles.premiumDeltaWrap}>
            {delta !== 0 && (
              <span className={arrowClassName} aria-hidden="true">
                {isIncrease ? '↑' : '↓'}
              </span>
            )}
            <span className={deltaClassName}>{formatMoney(delta)}</span>
          </span>
        </div>
        <div className={styles.premiumPreviousLine}>
          Previously: {formatMoney(oldValue)}
        </div>
      </div>
    ),
    value: String(newValue),
  };
}

function renderInfoValue(value: InfoValuePairDataType): React.ReactNode {
  if (value === null || value === undefined) {
    return '—';
  }

  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    React.isValidElement(value)
  ) {
    return value;
  }

  if (
    value &&
    typeof value === 'object' &&
    'display' in value &&
    value.display !== undefined
  ) {
    return value.display;
  }

  return '—';
}

interface TimelineProps {
  navPageItems: Array<{
    position: number;
    isObsolete: boolean;
    label?: string;
  }>;
  navPosition: number;
  skipObsolete: boolean;
  onSelectNavPosition?: (position: number) => void;
}

function VersionTimeline({
  navPageItems,
  navPosition,
  skipObsolete,
  onSelectNavPosition,
}: TimelineProps) {
  // Render in ascending position order (oldest left → newest right).
  const sorted = [...navPageItems].sort((a, b) => a.position - b.position);
  const visible = skipObsolete
    ? sorted.filter((item) => !item.isObsolete || item.position === navPosition)
    : sorted;

  const activeIndex = visible.findIndex(
    (item) => item.position === navPosition
  );

  // Auto-scroll the active node into view whenever it changes. Matches the
  // prototype's behaviour — keeps the active column visible when the strip
  // overflows the modal width. Optional-chain the method since jsdom (the
  // jest runtime) doesn't implement scrollIntoView, and we'd rather no-op
  // than crash the test.
  const activeNodeRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    activeNodeRef.current?.scrollIntoView?.({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [navPosition, skipObsolete]);

  if (visible.length === 0) return null;

  return (
    <div className={styles.timelineWrapper} aria-label="Version timeline">
      <div className={styles.timelineScroll}>
        <div
          className={styles.timelineNodes}
          role="navigation"
          aria-label="Version pagination"
        >
          {/* Progress track sits behind the nodes — anchored to .timelineNodes
              so left/right offsets resolve against the node-row width, not the
              wider scroll container. .timelineTrack has `left: 44px` (= centre
              of first node), so the filled child can start at left:0 and only
              its width needs to be calculated inline. */}
          <div className={styles.timelineTrack} aria-hidden="true">
            {activeIndex > 0 && (
              <div
                className={styles.timelineTrackFilled}
                style={{
                  // Each node column is 88px wide; the fill spans from the
                  // track's left edge (= centre of first node) for activeIndex
                  // columns, ending exactly at the active node's centre.
                  width: `${activeIndex * 88}px`,
                }}
              />
            )}
          </div>
          {visible.map((item) => {
            const isCurrent = item.position === navPosition;
            const isPast =
              !isCurrent &&
              visible.findIndex((v) => v.position === item.position) <
                activeIndex;

            const nodeClass = [
              styles.timelineNode,
              isCurrent
                ? styles.timelineNodeActive
                : isPast
                  ? styles.timelineNodePast
                  : styles.timelineNodeFuture,
              item.isObsolete ? styles.timelineNodeObsolete : '',
            ]
              .filter(Boolean)
              .join(' ');

            // Label looks like "1.4" → subtitle from the version type label isn't available
            // here without plumbing extra data, so we use the label itself as subtitle stub.
            // TODO DLC-590: plumb version type label + date into navPageItems so the timeline
            // can render meaningful date and subtitle text.
            const vLabel = item.label ?? String(item.position);

            return (
              <div key={item.position} className={styles.timelineColumn}>
                {/* Date label row — data not in navPageItems yet; blank for now */}
                <div className={styles.timelineDateLabel} aria-hidden="true" />

                {/* Circle node */}
                <button
                  ref={isCurrent ? activeNodeRef : undefined}
                  type="button"
                  className={nodeClass}
                  onClick={
                    !isCurrent && onSelectNavPosition
                      ? () => onSelectNavPosition(item.position)
                      : undefined
                  }
                  aria-current={isCurrent ? 'page' : undefined}
                  aria-label={`Version ${vLabel}${item.isObsolete ? ' (rolled back)' : ''}`}
                  title={
                    item.isObsolete
                      ? `Version ${vLabel} is rolled back`
                      : `Version ${vLabel}`
                  }
                  disabled={isCurrent}
                >
                  {vLabel}
                </button>

                {/* Version label below circle */}
                <div
                  className={[
                    styles.timelineVersionLabel,
                    isCurrent ? styles.timelineVersionLabelActive : '',
                    item.isObsolete ? styles.timelineVersionLabelObsolete : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  aria-hidden="true"
                >
                  v{vLabel}
                </div>

                {/* Subtitle: rolled-back indicator or blank */}
                <div className={styles.timelineSubtitle} aria-hidden="true">
                  {item.isObsolete ? (
                    <span className={styles.timelineRolledBack}>
                      ↺ Rolled Back
                    </span>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export const VersionDiffModal: React.FC<VersionDiffModalProps> = ({
  policyId,
  versionId,
  versionType,
  source,
  createdBy,
  createdAt,
  open,
  onClose,
  versionNumber,
  skipObsolete,
  onToggleSkipObsolete,
  onPrev,
  onNext,
  isPrevDisabled,
  isNextDisabled,
  navPosition,
  navTotal,
  navPageItems,
  onSelectNavPosition,
}) => {
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const handleCategoryToggle = useCallback((key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tagName = target?.tagName.toLowerCase();
      const isEditable =
        !!target?.isContentEditable ||
        tagName === 'input' ||
        tagName === 'textarea' ||
        tagName === 'select';
      if (isEditable || e.altKey || e.ctrlKey || e.metaKey) {
        return;
      }
      if (e.key === 'ArrowLeft' && onPrev && !isPrevDisabled) onPrev();
      if (e.key === 'ArrowRight' && onNext && !isNextDisabled) onNext();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onPrev, onNext, isPrevDisabled, isNextDisabled]);

  const {
    data: diff,
    loading,
    error,
  } = useDiffData(policyId, versionId, source);
  const timeZone = usePolicyContext().details.timeZone;

  const typeLabel = VERSION_TYPE_LABELS[versionType];
  const currentNavItem =
    navPosition !== undefined
      ? navPageItems?.find((item) => item.position === navPosition)
      : undefined;
  const termPrefix = currentNavItem?.label?.includes('.')
    ? `Term ${currentNavItem.label.split('.')[0]}, `
    : '';
  const versionPrefix =
    versionNumber !== undefined ? `Version ${versionNumber} — ` : '';
  const title =
    source === 'draft'
      ? `${termPrefix}${versionPrefix}${typeLabel} (Draft) Changes`
      : `${termPrefix}${versionPrefix}${typeLabel} Changes`;
  const hasContent =
    !!diff?.fullTermWrittenPremium || (diff?.categories.length ?? 0) > 0;
  const loadingLabel =
    source === 'draft' ? 'Loading draft changes' : 'Loading version changes';

  const showTimeline =
    navPosition !== undefined &&
    navTotal !== undefined &&
    navPageItems &&
    navPageItems.length > 1;

  return (
    <Modal variant="full" controlled={true} open={open} handleClose={onClose}>
      {(_close) => (
        <>
          <ModalContent>
            <div className={styles.modalBody}>
              {/* ── Toolbar (outside the card) ── */}
              <div className={styles.toolbarRow}>
                <button
                  type="button"
                  className={styles.backLink}
                  onClick={onClose}
                  aria-label="Back to Policy Details"
                >
                  <ArrowBackwardIcon
                    aria-hidden="true"
                    className={styles.backLinkIcon}
                  />
                  Back to Policy Details
                </button>

                {(onPrev || onNext) && (
                  <div className={styles.toolbarNav}>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={onPrev}
                      disabled={isPrevDisabled}
                      aria-label="Previous version"
                      className={styles.navBtn}
                      title={
                        isPrevDisabled
                          ? 'Already viewing the earliest version'
                          : 'View previous version'
                      }
                    >
                      <ArrowBackwardIcon
                        aria-hidden="true"
                        className={styles.navBtnIconPrev}
                      />
                      Prev Version
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={onNext}
                      disabled={isNextDisabled}
                      aria-label="Next version"
                      className={styles.navBtn}
                      title={
                        isNextDisabled
                          ? 'Already viewing the latest version'
                          : 'View next version'
                      }
                    >
                      Next Version
                      <ArrowForwardIcon
                        aria-hidden="true"
                        className={styles.navBtnIconNext}
                      />
                    </Button>
                  </div>
                )}
              </div>

              {/* ── Card ── */}
              <div className={styles.card}>
                <div className={styles.cardHead}>
                  <div className={styles.cardHeaderRow}>
                    {/* Left: title + subtitle */}
                    <div className={styles.headerStack}>
                      <h1 className={styles.modalTitle}>{title}</h1>
                      <p className={styles.viewingSubtitle}>
                        Viewing historical policy state.
                      </p>
                    </div>

                    {/* Right: hide rolled back toggle */}
                    {onToggleSkipObsolete && (
                      <div
                        className={styles.skipObsoleteControl}
                        role="group"
                        aria-label="Version navigation options"
                      >
                        <Checkbox
                          label=""
                          data-testid="toggle-skip-obsolete-switch"
                          name="skip-obsolete"
                          className={styles.toggleSwitch}
                          toggle={true}
                          onChange={onToggleSkipObsolete}
                          value={skipObsolete ?? false}
                          aria-label="Hide rolled back versions in navigation"
                        />
                        <span className={styles.skipObsoleteLabel}>
                          Hide rolled back
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Timeline strip — separates header from metadata */}
                {showTimeline && (
                  <div className={styles.cardTimelineSection}>
                    <VersionTimeline
                      navPageItems={navPageItems!}
                      navPosition={navPosition!}
                      skipObsolete={skipObsolete ?? false}
                      onSelectNavPosition={onSelectNavPosition}
                    />
                  </div>
                )}

                {!loading && !error && diff && (
                  <div className={styles.metadataSection}>
                    <div className={styles.metadataGrid}>
                      <div className={styles.metadataItem}>
                        <div className={styles.metadataLabel}>Created By</div>
                        <div className={styles.metadataValue}>
                          {selectCreatedBy(createdBy)}
                        </div>
                      </div>
                      <div className={styles.metadataItem}>
                        <div className={styles.metadataLabel}>Created Date</div>
                        <div className={styles.metadataValue}>
                          {renderInfoValue(
                            createdAt
                              ? formatSummaryElement(
                                  {
                                    label: 'Created Date',
                                    format: 'timestamp',
                                    value: createdAt,
                                  },
                                  timeZone
                                )
                              : '—'
                          )}
                        </div>
                      </div>
                      <div className={styles.metadataItem}>
                        <div className={styles.metadataLabel}>
                          Effective Date
                        </div>
                        <div className={styles.metadataValue}>
                          {renderInfoValue(
                            diff.fullTermWrittenPremium?.effectiveDate
                              ? formatSummaryElement(
                                  {
                                    label: 'Effective Date',
                                    format: 'timestamp',
                                    value:
                                      diff.fullTermWrittenPremium.effectiveDate,
                                  },
                                  timeZone
                                )
                              : '—'
                          )}
                        </div>
                      </div>
                      <div className={styles.metadataItem}>
                        <div className={styles.metadataLabel}>
                          Premium Impact
                        </div>
                        <div className={styles.metadataValue}>
                          {renderInfoValue(
                            diff.fullTermWrittenPremium
                              ? renderPremiumChange(diff.fullTermWrittenPremium)
                              : '—'
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {loading && (
                <div className={styles.loadingState} aria-live="polite">
                  <Loading size={24} />
                  <div className={styles.loadingLabel}>{loadingLabel}</div>
                </div>
              )}

              {error && (
                <div className={styles.errorState}>
                  <strong>Error:</strong> {error?.message}
                </div>
              )}

              {!loading && !error && diff && !hasContent && (
                <div className={styles.emptyState}>
                  <p className={styles.emptyStateHeadline}>
                    No field-level changes in this {source}.
                  </p>
                  <p className={styles.emptyStateHint}>
                    If this version is the result of a rollback, the policy
                    state was reverted to an earlier baseline — the rolled-back
                    versions between the baseline and this version are visible
                    in the timeline above. Open one of those to see the
                    underlying field changes that were undone.
                  </p>
                </div>
              )}

              {!loading && !error && diff && hasContent && (
                <div className={styles.sectionsList}>
                  <h2 className={styles.changesHeading}>
                    Changes in this Version
                  </h2>
                  {diff.categories.map((category) => (
                    <div key={category.key}>
                      <CategorySection
                        category={category}
                        isExpanded={expandedKeys.has(category.key)}
                        onToggle={handleCategoryToggle}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </ModalContent>
        </>
      )}
    </Modal>
  );
};

export default VersionDiffModal;
